import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMessage } from "../lib/api";

const HERO =
  "https://images.pexels.com/photos/11815816/pexels-photo-11815816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/dashboard";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(name.trim(), email.trim(), password, phone.trim() || null);
      toast.success("Account created");
      nav(next);
    } catch (err) {
      toast.error(errMessage(err, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[--bg]">
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(10,10,10,0.85), rgba(0,47,167,0.55)), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="overline text-white/70">// Wholesale recon</div>
          <div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-none mb-6">
              Get on the<br/>off-market<br/>side first.
            </h1>
            <p className="text-white/80 max-w-md text-base">
              Score every market, surface the leads, and chase what no other investor
              has touched yet.
            </p>
          </div>
          <div className="font-mono text-xs text-white/60">v1.0 · WholesaleHQ</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          data-testid="register-form"
          className="w-full max-w-sm space-y-6 animate-fade-up"
        >
          <div>
            <div className="overline mb-3">Create account</div>
            <h2 className="font-display text-3xl font-bold">Start hunting deals.</h2>
            <p className="text-sm text-[--ink-soft] mt-2">
              Already have one?{" "}
              <Link to="/login" data-testid="login-link" className="text-[--accent] underline underline-offset-2">
                Sign in
              </Link>
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">Full name</span>
              <input
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">Email</span>
              <input
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">
                Mobile <span className="text-[--ink-muted] font-normal">(optional · for SMS alerts)</span>
              </span>
              <input
                data-testid="register-phone-input"
                type="tel"
                placeholder="+15551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium block mb-1.5">Password</span>
              <input
                data-testid="register-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
            </label>
          </div>

          <button
            type="submit"
            data-testid="register-submit-button"
            disabled={busy}
            className="btn-primary w-full"
          >
            {busy ? "Creating…" : "Create account →"}
          </button>
        </form>
      </div>
    </div>
  );
}
